import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Download } from "lucide-react";
import { api, errMsg, fmtBytes, BACKEND } from "@/lib/api";
import { Panel, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

function Row({ x }) {
  const tone = x.crashed ? "red" : x.rc === 0 ? "green" : "amber";
  const what = x.crashed ? `crashed (signal ${x.signal}) after ${x.secs}s` : x.rc === 0 ? `ok · ${x.segments} segments in ${x.secs}s` : `exited rc=${x.rc}`;
  return (
    <tr data-testid={`crash-test-row-${x.id}`} className="border-b border-slate-800/60 align-top">
      <td className="py-1.5 pr-3 text-slate-200">{x.label}<div className="font-mono text-[10px] text-slate-500">{x.version}</div></td>
      <td className="py-1.5 pr-3"><Pill tone={tone}>{what}</Pill></td>
      <td className="py-1.5 font-mono text-[10px] text-slate-500 max-w-[360px] break-all">{x.kernel || (x.tail || []).join(" · ") || "—"}</td>
    </tr>
  );
}

function Results({ d, onInstall }) {
  return (
    <div className="mt-3 space-y-2" data-testid="crash-test-results">
      {d.sample && <div className="text-[11px] text-slate-500">Sample: {d.sample}</div>}
      {d.rows?.length > 0 && <table className="w-full text-xs"><tbody>{d.rows.map((x) => <Row key={x.id} x={x} />)}</tbody></table>}
      {d.verdict && <div data-testid="crash-test-verdict" className="rounded border border-cyan-900 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-200">{d.verdict}</div>}
      {d.candidate_error && <div className="text-xs text-red-400">ffmpeg 8.1 download failed: {d.candidate_error}</div>}
      {d.error && <div data-testid="crash-test-error" className="text-xs text-red-400">{d.error}</div>}
      {d.gdb && (
        <details data-testid="crash-test-gdb" className="rounded border border-slate-800 bg-slate-950/60" open={d.gdb.reproduced}>
          <summary className="cursor-pointer px-3 py-1.5 text-xs text-slate-300">gdb backtrace · {d.gdb.variant} · {!d.gdb.available ? "gdb not installed" : d.gdb.reproduced ? "crash reproduced" : "did not crash under gdb"}</summary>
          <pre data-testid="crash-test-gdb-output" className="max-h-96 overflow-auto px-3 py-2 font-mono text-[10px] text-slate-400 whitespace-pre-wrap">{d.gdb.output}</pre>
        </details>
      )}
      {d.candidate_ok && <Button data-testid="install-candidate-btn" size="sm" className="bg-cyan-700 hover:bg-cyan-600" onClick={onInstall}>Install ffmpeg 8.1 as the bundled build</Button>}
    </div>
  );
}

export function CrashLab({ cid, running }) {
  const [d, setD] = useState(null);
  const [cand, setCand] = useState(true);
  const load = () => api.get(`/admin/channels/${cid}/crash-test`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (d?.status !== "running") return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [d?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    try { await api.post(`/admin/channels/${cid}/crash-test`, { candidate: cand }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const install = async () => {
    try { const r = await api.post(`/admin/ffmpeg/install-candidate`); toast.success(r.data.message); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const busy = d?.status === "running";
  return (
    <Panel testid="crash-lab-panel" title="ffmpeg crash test" right={busy && <span data-testid="crash-test-step" className="font-mono text-[11px] text-amber-400">{d.step}</span>}>
      <p className="text-xs text-slate-400">Records 60 s of this channel's source with its exact arrival timing (and uses the bytes saved at the last crash, if any), replays them on this server through each ffmpeg build and mode, at full speed and at the original live timing, then re-runs the first crash under gdb for a backtrace. The live channel is not touched.</p>
      <div className="flex items-center gap-4 mt-3">
        <Button data-testid="crash-test-run-btn" size="sm" variant="outline" className="border-slate-700" disabled={busy || !running} onClick={start}><FlaskConical size={13} /> {busy ? "Testing…" : "Run crash test"}</Button>
        <label className="flex items-center gap-2 text-xs text-slate-400"><Checkbox data-testid="crash-test-candidate" checked={cand} onCheckedChange={(v) => setCand(!!v)} /> include ffmpeg 8.1 (downloads ~140 MB once)</label>
      </div>
      {(d?.samples || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="crash-samples">
          {d.samples.map((s) => (
            <a key={s.name} data-testid={`crash-sample-${s.name}`} href={`${BACKEND}/api/admin/channels/${cid}/crash-samples/${s.name}`} className="text-[11px] text-cyan-400 hover:underline flex items-center gap-1">
              <Download size={11} /> {s.name} · {fmtBytes(s.size)}{s.signal ? ` · signal ${s.signal}` : ""}
            </a>
          ))}
        </div>
      )}
      {d && d.status !== "idle" && <Results d={d} onInstall={install} />}
    </Panel>
  );
}
